import { omit, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { ticketAttachmentsSet as ticketAttachmentsSetAction } from '~/actions/ticket';
import { getTicketAttachments } from '~/api/tickets';
import {
  EMAIL_MODAL_SUBJECT_INPUT_TESTID,
  EMAIL_MODAL_TO_INPUT_TESTID,
} from '~/common/constants/testids/email-modal';
import TicketFilesSelect from '~/components/Modals/EmailModal/TicketFilesSelect';
import EcInput from '~/components/Shared/EcInput/EcInput';
import FormLabel from '~/components/Shared/FormLabel/FormLabel';
import { ButtonGroup } from '~/eds/src/components/ButtonGroup/ButtonGroup';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { OutboundEmailFileType } from '~/enums/OuboundEmailFileType';
import { PartySelectField } from '~/features/turn-tracking/PartySelectField';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import Divider from '~/ui/components/Divider/Divider';
import EmailSelect from '~/ui/components/EmailSelect/EmailSelect';
import FileInput from '~/ui/components/FileInput/FileInput';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import * as enums from '~/ui/enums/input';
import { sortByStringValue } from '~/utils/array';
import { getFileExtension } from '~/utils/files';
import { validateEmail } from '~/utils/strings';
import { getVersionFileName } from '~/utils/ticket';
import { getUserName } from '~/utils/user';

import { attachmentPropTypes } from './Attachment';
import OutboundAttachment from './OutboundAttachment';

function OutboundEmailModal({
  actionButton,
  title,
  visible,
  onHide,
  onUpdate,
  // connected
  users,
  currentUser,
  ticket,
  ticketAttachmentsSet,
}) {
  const intl = useIntl();
  const currentUserName = getUserName(users[currentUser.id], intl);
  const { document, documentVersions } = ticket;

  const currentVersion = documentVersions.find(
    (d) => d.versionNumber === document.version,
  );
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const defaultData = {
    to: [],
    cc: [],
    bcc: [],
    subject: intl.formatMessage(
      {
        id: 'OutboundEmailModal.defaultSubject',
        defaultMessage: 'Document shared: {fileName} from {userName}',
        description:
          'Default email subject line. Example: fileName = "Document Name", userName = "Document Name"',
      },
      {
        fileName: getVersionFileName(document, currentVersion),
        userName: currentUserName,
      },
    ),
    body: intl.formatMessage(
      {
        id: 'OutboundEmailModal.defaultBody',
        defaultMessage:
          'Hi,\n\nPlease find documents for your review attached.\n\nSincerely,\n{userName}',
        description:
          'Default email body text. Example: userName = "Document Name"',
      },
      {
        userName: currentUserName,
      },
    ),
  };

  const [draft, setDraft] = useState(defaultData);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // update the draft data when opening the modal
  useEffect(() => {
    setDraft(defaultData);
  }, [visible]);

  useAsync(getTicketAttachments, ticket.id, {
    condition: visible,
    deps: [visible],
    successHandler: async (attachmentsResponse) => {
      ticketAttachmentsSet(attachmentsResponse);
    },
    errorToastMessage: intl.formatMessage({
      id: 'OutboundEmailModal.errorFetchingAttachments',
      defaultMessage: 'There was an error when fetching for the attachments.',
      description: 'Error message when fetching ticket attachments fails',
    }),
  });

  useEffect(() => {
    const initialDefaultAttachment = generateDefaultAttachment();
    onUpdate(defaultData);
    if (initialDefaultAttachment) {
      setSelectedFiles([{ type: 'version', id: initialDefaultAttachment.id }]);
    }
  }, []);

  function generateDefaultAttachment() {
    const latestVersion = ticket.documentVersions[0];
    if (!latestVersion) return null;

    return {
      type: OutboundEmailFileType.ExistingVersion,
      id: latestVersion.id,
      name: getVersionFileName(document, currentVersion),
      version: latestVersion.versionNumber,
      uploadedBy: latestVersion.userId,
      uploadDate: latestVersion.lastUpdated,
      size: latestVersion.fileSize,
    };
  }

  function generateVersionAttachments() {
    return orderBy(
      selectedFiles
        .filter(
          (selectedFile) =>
            selectedFile.type === OutboundEmailFileType.ExistingVersion,
        )
        .map((selectedFile) => {
          const newVersion = ticket.documentVersions.find(
            (version) => version.id === selectedFile.id,
          );

          return {
            type: OutboundEmailFileType.ExistingVersion,
            id: newVersion.id,
            name: getVersionFileName(document, newVersion),
            version: newVersion.versionNumber,
            uploadedBy: newVersion.userId,
            uploadDate: newVersion.lastUpdated,
            size: newVersion.fileSize,
          };
        }),
      ['version'],
      ['desc'],
    );
  }

  function generateFileAttachments() {
    return selectedFiles
      .filter(
        (selectedFile) =>
          selectedFile.type === OutboundEmailFileType.ExistingAttachment,
      )
      .map((selectedFile) => {
        const newAttachment = ticket.attachments.find(
          (attachment) => attachment.id === selectedFile.id,
        );

        return {
          type: OutboundEmailFileType.ExistingAttachment,
          id: newAttachment.id,
          name: `${newAttachment.fileName}${newAttachment.fileType}`,
          version: newAttachment.versionNumber,
          uploadedBy: newAttachment.userId,
          uploadDate: newAttachment.lastUpdated,
          size: newAttachment.fileSize,
        };
      });
  }

  function generateUploadedAttachments(files) {
    return files.map((file) => ({
      type: OutboundEmailFileType.UploadedFile,
      id: uuid(),
      name: file.name,
      version: null,
      file: file,
      uploadedBy: null,
      uploadDate: null,
      size: file.size,
    }));
  }

  function handleHide() {
    setDraft(defaultData);
    onUpdate(defaultData);
    setShowCc(false);
    setShowBcc(false);
    onHide();
    setUploadedFiles([]);
    const defaultVersionAttachment = generateDefaultAttachment();
    setSelectedFiles([
      {
        type: OutboundEmailFileType.ExistingVersion,
        id: defaultVersionAttachment.id,
      },
    ]);
  }

  useEffect(() => {
    if (
      document.version &&
      ticket.documentVersions &&
      ticket.documentVersions.length > 0
    ) {
      const defaultVersionAttachment = generateDefaultAttachment();
      setSelectedFiles([
        {
          type: OutboundEmailFileType.ExistingVersion,
          id: defaultVersionAttachment.id,
        },
      ]);
    }
  }, [document.version, ticket.documentVersions]);

  useEffect(() => {
    const selectedVersions = generateVersionAttachments();
    const selectedAttachments = generateFileAttachments();

    const platformAttachments = [...selectedVersions, ...selectedAttachments];

    const newAttachments = [...platformAttachments, ...uploadedFiles];

    setAttachments(newAttachments);
    // remove unused fields and update data
    const attachmentsOmitted = platformAttachments.map((attachment) =>
      omit(attachment, ['version', 'uploadedBy', 'uploadDate', 'size']),
    );

    onUpdate((data) => ({
      ...data,
      attachments: attachmentsOmitted,
      uploadedAttachments: uploadedFiles.map(
        (uploadedAttachment) => uploadedAttachment.file,
      ),
    }));
  }, [selectedFiles, uploadedFiles]);

  function hasInvalidEmail(recipients) {
    return recipients.some((recipient) => !validateEmail(recipient.value));
  }

  function getEmailError(recipients) {
    if (hasInvalidEmail(recipients)) {
      return intl.formatMessage({
        id: 'OutboundEmailModal.invalidEmailError',
        defaultMessage:
          'There is an error with your email list, please double check and add the right email.',
        description: 'Error message for invalid email addresses',
      });
    }
    return '';
  }

  function disableShareButton(draft, body) {
    return (
      draft.to.length + draft.cc.length + draft.bcc.length === 0 ||
      body.length > enums.CHARACTER_INPUT_LIMIT_LONG ||
      body.length === 0 ||
      hasInvalidEmail(draft.to) ||
      hasInvalidEmail(draft.cc) ||
      hasInvalidEmail(draft.bcc)
    );
  }

  function getEmailBodyError(body) {
    if (!body) {
      return intl.formatMessage({
        id: 'OutboundEmailModal.missingBodyError',
        defaultMessage: 'Missing characters. Need as least 1 character.',
        description: 'Error message for empty email body',
      });
    }
    return '';
  }

  function onUploadFiles(files) {
    if (files) {
      const fileList = generateUploadedAttachments(files);
      setUploadedFiles((filesInList) => [...filesInList, ...fileList]);
    }
  }

  function handleUpdateDraft(key, value) {
    const updatedDraft = { ...draft, [key]: value };
    setDraft(updatedDraft);
    const updatedData = {
      to: updatedDraft.to.map((item) => item.value),
      cc: updatedDraft.cc.map((item) => item.value),
      bcc: updatedDraft.bcc.map((item) => item.value),
      subject: updatedDraft.subject,
      body: updatedDraft.body,
      turnTracking: updatedDraft.turnTracking,
    };
    onUpdate((shareData) => ({
      ...shareData,
      ...updatedData,
    }));
  }

  const { to, cc, bcc, subject, body } = draft;
  const disabled = disableShareButton(draft, body);

  const findDocDocxInAttachment = () => {
    return attachments.some((file) => {
      const fileExtension = getFileExtension(file.name);
      return (
        fileExtension === FileExtensionType.Docx ||
        fileExtension === FileExtensionType.Doc
      );
    });
  };

  const isAttachmentSizeGreaterThan10MB = () => {
    let totalSizeInBytes = 0;
    const tenMBInBytes = 10 * 1024 * 1024;
    attachments.forEach((file) => (totalSizeInBytes += file.size));
    return totalSizeInBytes > tenMBInBytes;
  };

  const renderWarning = () => {
    let warningComponent = null;
    const hasDocOrDocx = findDocDocxInAttachment();
    const isSizeGreaterThanThan10MB = isAttachmentSizeGreaterThan10MB();

    let text = '';
    if (hasDocOrDocx && !isSizeGreaterThanThan10MB) {
      text = intl.formatMessage({
        id: 'OutboundEmailModal.commentsWarning',
        defaultMessage:
          'Your document may contain comments. Please review and/or remove any comments from your file prior to sharing.',
        description: 'Warning about document comments',
      });
    } else if (!hasDocOrDocx && isSizeGreaterThanThan10MB) {
      text = intl.formatMessage({
        id: 'OutboundEmailModal.sizeWarning',
        defaultMessage:
          'If your attachments exceed the size allowed by the email provider, your email may fail to send.',
        description: 'Warning about attachment size',
      });
    } else if (hasDocOrDocx && isSizeGreaterThanThan10MB) {
      text = intl.formatMessage({
        id: 'OutboundEmailModal.combinedWarning',
        defaultMessage:
          'If your email attachments exceed the size allowed by the email provider, your email may fail to send. Additionally, your document may contain comments. Please review and/or remove comments from your file prior to sharing.',
        description: 'Warning about both size and comments',
      });
    }
    if (text) {
      warningComponent = <StatusMessage status="warning" message={text} />;
    }

    return warningComponent;
  };
  const options = useMemo(() => {
    return Object.values(users)
      .map((user) => ({
        value: user.email,
        label: `${getUserName(user, intl)} (${user.email})`,
        chipLabel: getUserName(user, intl),
      }))
      .sort(sortByStringValue('label'));
  }, [users]);

  const removeAttachment = (id, type) => {
    const updateFunction =
      type !== OutboundEmailFileType.UploadedFile
        ? setSelectedFiles
        : setUploadedFiles;
    updateFunction((prevAttachments) =>
      prevAttachments.filter((attachment) => attachment.id !== id),
    );
  };

  const renderAttachments = () => {
    return attachments.map((attachment, index) => (
      <FlexLayout flexDirection="column" key={index}>
        <OutboundAttachment
          onRemove={() => removeAttachment(attachment.id, attachment.type)}
          {...attachment}
        />
        {index < attachments.length - 1 && <Divider />}
      </FlexLayout>
    ));
  };

  return (
    <Modal
      title={title}
      width="l"
      disableHideOnEscape
      disableHideOnClickOutside
      cancelText={null}
      isVisible={visible}
      onHide={handleHide}
      primaryAction={{
        text: actionButton?.text,
        variant: actionButton?.variant,
        disabled,
        onClick: () => {
          actionButton?.promise();
          handleHide();
        },
        tooltip: actionButton?.tooltip,
      }}
    >
      <FlexLayout flexDirection="column" space={8} sx={{ textAlign: 'left' }}>
        <FlexLayout justifyContent="space-between">
          <FormLabel
            className={EMAIL_MODAL_TO_INPUT_TESTID}
            input={
              <EmailSelect
                name="to recipients"
                inputId="emailSelect-to"
                onChange={(updatedEmails) => {
                  handleUpdateDraft('to', updatedEmails);
                }}
                values={to}
                options={options}
                errorMessage={getEmailError(to)}
              />
            }
            label={intl.formatMessage({
              id: 'OutboundEmailModal.toRecipientsLabel',
              defaultMessage: 'To Recipients',
              description: 'Label for email to recipients field',
            })}
          />
          <ButtonGroup
            actions={[
              {
                text: intl.formatMessage({
                  id: 'OutboundEmailModal.ccButton',
                  defaultMessage: 'CC',
                  description: 'CC button text',
                }),
                onClick: () => setShowCc(!showCc),
                disabled: showCc,
              },
              {
                text: intl.formatMessage({
                  id: 'OutboundEmailModal.bccButton',
                  defaultMessage: 'BCC',
                  description: 'BCC button text',
                }),
                onClick: () => setShowBcc(!showBcc),
                disabled: showBcc,
              },
            ]}
          />
        </FlexLayout>
        {showCc && (
          <FormLabel
            className={EMAIL_MODAL_TO_INPUT_TESTID}
            input={
              <EmailSelect
                inputId="emailSelect-cc"
                onChange={(updatedEmails) => {
                  handleUpdateDraft('cc', updatedEmails);
                }}
                values={cc}
                options={options}
                errorMessage={getEmailError(cc)}
              />
            }
            label={intl.formatMessage({
              id: 'OutboundEmailModal.ccLabel',
              defaultMessage: 'CC',
              description: 'Label for CC field',
            })}
          />
        )}
        {showBcc && (
          <FormLabel
            className={EMAIL_MODAL_TO_INPUT_TESTID}
            input={
              <EmailSelect
                inputId="emailSelect-bcc"
                onChange={(updatedEmails) => {
                  handleUpdateDraft('bcc', updatedEmails);
                }}
                values={bcc}
                options={options}
                errorMessage={getEmailError(bcc)}
              />
            }
            label={intl.formatMessage({
              id: 'OutboundEmailModal.bccLabel',
              defaultMessage: 'BCC',
              description: 'Label for BCC field',
            })}
          />
        )}
        <PartySelectField
          workflowId={ticket.workflowId}
          onChange={(value) => {
            handleUpdateDraft('turnTracking', { partyId: value });
          }}
          errorMessage={{
            message: intl.formatMessage({
              id: 'OutboundEmailModal.responsiblePartyErrorTitle',
              defaultMessage: 'Try again in a few minutes.',
              description: 'Error message for responsible party selection',
            }),
            title: intl.formatMessage({
              id: 'OutboundEmailModal.responsiblePartyError',
              defaultMessage:
                'We’re experiencing difficulty loading Responsible Parties.',
              description: 'Title for responsible party error',
            }),
          }}
        />
        <FormLabel
          className={EMAIL_MODAL_SUBJECT_INPUT_TESTID}
          input={
            <EcInput
              input={{
                value: subject,
                onChange: (e) => handleUpdateDraft('subject', e.target.value),
              }}
              width="100%"
            />
          }
          label={intl.formatMessage({
            id: 'OutboundEmailModal.subjectLabel',
            defaultMessage: 'Subject',
            description: 'Label for email subject field',
          })}
        />
        <FormField
          error={getEmailBodyError(body)}
          label={intl.formatMessage({
            id: 'OutboundEmailModal.messageLabel',
            defaultMessage: 'Message',
            description: 'Label for email message field',
          })}
          onChange={(value) => {
            handleUpdateDraft('body', value);
          }}
          placeholder={intl.formatMessage({
            id: 'OutboundEmailModal.messagePlaceholder',
            defaultMessage: 'Please add a message to send.',
            description: 'Placeholder text for email message field',
          })}
          input={TextArea}
          inputProps={{
            name: 'Message to Send',
            value: body,
            placeholder: intl.formatMessage({
              id: 'OutboundEmailModal.messagePlaceholder',
              defaultMessage: 'Please add a message to send.',
              description: 'Placeholder text for email message field',
            }),
          }}
        />
        <FlexLayout flexDirection="column" space={2}>
          <FlexLayout flexDirection="column" space={2}>
            <Text variant="body-bold">
              <FormattedMessage
                id="OutboundEmailModal.attachmentsTitle"
                defaultMessage="Attachments"
                description="Title for attachments section"
              />
            </Text>
            {renderWarning()}
          </FlexLayout>
          <FlexLayout flexDirection="column" space={6}>
            <FlexLayout flexDirection="column">
              {renderAttachments()}
            </FlexLayout>
          </FlexLayout>
          <FlexLayout mt={4} space={4}>
            <TicketFilesSelect
              value={selectedFiles}
              onSelect={setSelectedFiles}
            />
            <FileInput
              acceptedFiles={null}
              enableMultiple
              enableDropzone={false}
              label={intl.formatMessage({
                id: 'OutboundEmailModal.uploadLabel',
                defaultMessage: 'Upload',
                description: 'Label for file upload button',
              })}
              onChange={onUploadFiles}
            />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
    </Modal>
  );
}

OutboundEmailModal.propTypes = {
  actionButton: PropTypes.object,
  attachments: PropTypes.arrayOf(attachmentPropTypes.isRequired),
  title: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = ({ ticket }) => ({ ticket });

export default connect(mapStateToProps, {
  ticketAttachmentsSet: ticketAttachmentsSetAction,
})(withUsers(withCurrentUser(OutboundEmailModal)));
